import MojitoCore from 'mojito/core';
import MojitoServices from 'mojito/services';

import {isJsonContentType} from '#core/utils/utils.js';
import {isEmpty} from '#core/utils/config-utils.js';
import {convertToHttpsProtocol} from '#core/utils/url-utils.js';
import {Logger} from '#core/utils/logger.js';

import {actions as aliraActions} from './store/slice';

const TransactionsTypes = MojitoCore.Services.Transactions.types;
const AuthenticationActions = MojitoServices.Authentication.actions;
const storageService = MojitoCore.Services.Storage.service;
const {dispatch} = MojitoCore.Services.redux.store;
const log = Logger('AliraSSOService');

// //////////////////////////////////////////
// Constants

const COOKIE_KEYS = {
    PLAYER_ID: 'playerId',
    SESSION_ID: 'sessionId',
    AUTH_TOKEN: 'auth_token',
};

// //////////////////////////////////////////
// Implementation

export default class AliraSSOService extends MojitoServices.Authentication.AbstractSSOService {
    constructor(loginIntegrationBaseURL) {
        super();

        this.loginIntegrationBaseURL = convertToHttpsProtocol(loginIntegrationBaseURL);
    }

    /**
     * Check if logged in through SSO.
     * Typical implementations would check for some sort of cookie to determine if logged in.
     * If a valid userName and sessionToken is provided the user is considered logged in.
     *
     * @param {function} callback - Success response callback,
     * should be called if user id and session token has been found in cookies or elsewhere.
     */
    checkIfLoggedInBySSO(callback) {
        log.info('checkIfLoggedInBySSO');

        // NB: Not verified with real Alira cookies!
        // Investigate cookies to determine if logged in
        const playerId = this.getCookie(COOKIE_KEYS.PLAYER_ID);
        const sessionId = this.getCookie(COOKIE_KEYS.SESSION_ID);

        if (playerId && sessionId) {
            log.info('logged in');
            this.loadTimeInfo(
                time => {
                    const dateFormatted = new Date(time).toISOString();
                    log.info('last login time:', dateFormatted);
                    callback(playerId, sessionId, dateFormatted);
                },
                () => {
                    callback(playerId, sessionId);
                }
            );
        } else {
            storageService.removeCookie(COOKIE_KEYS.AUTH_TOKEN);
            dispatch(AuthenticationActions.logout());
        }
    }

    loadTimeInfo(success, fail) {
        log.info('load timing info');

        const sessionId = this.getCookie(COOKIE_KEYS.SESSION_ID);
        const url = `${this.loginIntegrationBaseURL}/times.jsp?json=true`;

        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: `sessionId=${sessionId}`,
        })
            .then(response => {
                if (response.ok && isJsonContentType(response)) {
                    return response.json();
                }
                return Promise.reject('load timing info failed');
            })
            .then(data => {
                if (data.currentSessionStartTime) {
                    return success(data.currentSessionStartTime);
                }
                return fail();
            })
            .catch(error => {
                log.info(error);
                return fail();
            });
    }

    /**
     * Login request using username and password
     *
     * @param {Object} credentials - Credentials
     * @param {Object} credentials.data - Object with credentials
     * @param {string} credentials.data.publicFactor - Username
     * @param {string} credentials.data.privateFactor - Password
     * @param {function} successCallback - Login success callback
     * @param {function} errorCallback - Login error callback
     */
    login(credentials, successCallback, errorCallback) {
        log.info('logging in');

        const url = `${this.loginIntegrationBaseURL}/login.jsp?json=true`;
        const {publicFactor, privateFactor} = credentials.data;

        fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
            },
            credentials: 'include',
            body: `username=${publicFactor}&password=${privateFactor}`,
        })
            .then(response => {
                if (response.ok && isJsonContentType(response)) {
                    return response.json();
                }
                return Promise.reject(response);
            })
            .then(data => {
                if (data.playerId && data.sessionId) {
                    dispatch(aliraActions.loginSuccess({username: publicFactor, lastLoginTime: data.lastLogin}));
                    const dateFormatted = new Date(data.currentSessionStartTime).toISOString();
                    log.info('last login time:', dateFormatted);
                    successCallback(data.playerId.toString(), data.sessionId, dateFormatted);
                } else {
                    return Promise.reject(data);
                }
            })
            .catch(error => {
                log.error('SSO login failed. Fallback to login by username.', error);
                dispatch(aliraActions.loginFailed());
                // Unknown error, look at login-dialog-content.jsx > ERROR_TRANSLATION_MAP in mojito
                errorCallback({type: TransactionsTypes.ERROR_CODE.UNKNOWN});
            });
    }

    /**
     * Logout request
     */
    logout() {
        log.info('logging out');

        // Session id from cookie
        const sessionId = this.getCookie(COOKIE_KEYS.SESSION_ID);

        if (!isEmpty(sessionId)) {
            const url = `${this.loginIntegrationBaseURL}/logout.jsp?json=true`;
            fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                },
                credentials: 'include',
                body: `sessionId=${sessionId}`,
            }).catch(error => {
                log.info(`log out failed: ${error}`);
            });
        }
    }

    clearCookies() {
        storageService.removeCookie(COOKIE_KEYS.PLAYER_ID);
        storageService.removeCookie(COOKIE_KEYS.SESSION_ID);
    }

    getCookie(key) {
        if (!key) {
            return null;
        }

        const match = document.cookie.match(new RegExp(key + '=([^;]+)'));
        return match ? match[1] : null;
    }
}
